import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { Route, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { MenuListItemComponent } from './menu-list-item/menu-list-item.component';
import { AuthenticationResult } from '../../../models/AuthenticationResult';
import { SideNavRoute } from '../../../models/SideNavRoute';
import { User } from '../../../models/User';
import { AuthenticationService } from '../../../services/authentication.service';
import { SideNavService } from '../../../services/side-nav.service';
import { CoreModule } from '../../core.module';
import { NavigationBarComponent } from '../navigation-bar/navigation-bar.component';

@Component({
  selector: 'stine-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  standalone: true,
  imports: [CommonModule, NavigationBarComponent, CoreModule, MenuListItemComponent],
})
export class NavigationComponent implements AfterViewInit, OnDestroy {
  @ViewChild('drawer') public sideNav?: MatSidenav;
  @ViewChild('sideNavContainer')
  public sideNavContainer!: MatSidenavContainer;
  public sideNavRoutes: SideNavRoute[] = [];
  public dashboardRoute: SideNavRoute;
  public currentUser?: User;
  protected currentYear: number;
  private lastOffset: number;
  private subscriptions: Subscription[] = [];
  private sideNavService = inject(SideNavService);
  private authService = inject(AuthenticationService);
  private router = inject(Router);

  constructor() {
    this.currentYear = new Date().getFullYear();
    this.lastOffset = 0;
    this.dashboardRoute = {
      icon: 'dashboard',
      route: '',
      title: 'Dashboard',
      hidden: false,
      // roles?: string[];
      hasContent: false,
      children: [],
      expandable: false,
      // level?: number;
      // class?: string;
      disabled: false,
    };

    this.subscriptions.push(
      this.authService.currentAuth$.subscribe((result: AuthenticationResult) => {
        this.currentUser = result?.user || null;
        if (result && result.user) {
          this.sideNavRoutes = this.loadNavigationList(this.router.config);
        } else {
          this.currentUser = undefined;
        }
      }),
    );

    this.subscriptions.push(
      this.sideNavService.scrollTo.subscribe((direction: string) => {
        if (this.sideNavContainer?.scrollable) {
          this.lastOffset =
            this.sideNavContainer?.scrollable?.getElementRef().nativeElement.scrollHeight;
          if (direction === 'bottom') {
            this.sideNavContainer.scrollable.getElementRef().nativeElement.scrollTop = 0;
          } else {
            this.sideNavContainer.scrollable.getElementRef().nativeElement.scrollTop = 400;
          }
        }
      }),
    );
    this.sideNavRoutes = this.loadNavigationList(this.router.config);
  }

  isSidenavOpened = false;

  ngOnInit(): void {
    this.subscriptions.push(
      this.authService.currentAuth$.subscribe((result: AuthenticationResult) => {
        this.currentUser = result?.user || null;
        if (result && result.user) {
          this.sideNavRoutes = this.loadNavigationList(this.router.config);
        } else {
          this.currentUser = undefined;
        }
      }),
    );
  }

  toggleSidenav() {
    this.isSidenavOpened = !this.isSidenavOpened;
  }

  ngAfterViewInit(): void {
    this.sideNavService.sideNav = this.sideNav;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private loadNavigationList(routes: Route[], parentSideNavRoute?: SideNavRoute): SideNavRoute[] {
    const routesWithTitles = this.getRoutesWithTitles(routes);
    return routesWithTitles
      .map((route) => {
        if (this.shouldDisplayRoute(route)) {
          const navigationRoute: SideNavRoute = {
            route: (parentSideNavRoute?.route ?? '') + '/' + route.path,
            title: route.data?.['title'],
            icon: route.data?.['icon'],
            roles: route.data?.['roles'],
            hasContent: !!route.loadComponent() || !!route.component,
            class: route?.data?.['class'],
            disabled: route?.data?.['disabled'],
          };
          const childRoutes = this.getRoutesWithTitles(route.children || []);
          if (childRoutes?.length > 0) {
            navigationRoute.children = this.loadNavigationList(childRoutes, navigationRoute);
          }

          return navigationRoute;
        }
        return undefined;
      })
      .filter((route) => route !== undefined);
  }

  private shouldDisplayRoute(route: Route): boolean {
    let displayRoute = false;
    if (this.authService.isUserAuthorized(route.data) && !route.data?.['hidden']) {
      if (route.component || route.loadComponent()) {
        displayRoute = true;
      }
      route.children?.forEach((childRoute) => {
        displayRoute = displayRoute || this.shouldDisplayRoute(childRoute);
      });
    }
    return displayRoute;
  }

  private getRoutesWithTitles(routes: Route[]): Route[] {
    return routes?.filter((route) => route.data?.['title']);
  }
}
